import { useState } from 'react';
import { Box, Typography, TextField, IconButton, Autocomplete, Grid } from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';

interface Row { internalEvent: string; outboundEvent: string }

/**
 * Rows are local state, not derived from `value`: a half-typed row has no outbound
 * name yet, so round-tripping through the record on every keystroke would delete it
 * mid-edit.
 */
const HELP: Record<string, string> = {
  facebook_capi: 'Sets the event name sent to the Graph API.',
  bing_capi: 'Must match a Microsoft conversion goal name exactly — a name that does not exist on the tag is accepted and then never attributes. Unmapped events are not sent.',
  twitter_capi: 'Sets the X conversion event name reported for this event type.',
};

export default function EventMappingEditor({ value, onChange, conversionTypeNames, outboundOptions, label, type }: {
  value?: Record<string, string>;
  onChange: (mapping: Record<string, string> | undefined) => void;
  conversionTypeNames: string[];
  outboundOptions?: string[];
  label: string;
  type?: string;
}) {
  const [rows, setRows] = useState<Row[]>(
    () => Object.entries(value || {}).map(([k, v]) => ({ internalEvent: k, outboundEvent: v })),
  );

  const emit = (next: Row[]) => {
    setRows(next);
    const mapping = Object.fromEntries(
      next.filter(r => r.internalEvent.trim() && r.outboundEvent.trim())
        .map(r => [r.internalEvent.trim(), r.outboundEvent.trim()]),
    );
    onChange(Object.keys(mapping).length ? mapping : undefined);
  };

  const setRow = (i: number, patch: Partial<Row>) =>
    emit(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <IconButton size="small" color="primary"
          onClick={() => emit([...rows, { internalEvent: '', outboundEvent: '' }])}>
          <AddCircleOutline sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Maps a conversion type to the outbound event name. Available as {'{{mapped_event}}'} in URLs.
        {type && HELP[type] ? ` ${HELP[type]}` : ''}
      </Typography>

      {rows.map((row, i) => (
        <Grid container spacing={1} key={i} sx={{ alignItems: 'center', mb: 1.25 }}>
          <Grid size={{ xs: 5 }}>
            <Autocomplete
              size="small" freeSolo options={conversionTypeNames}
              value={row.internalEvent}
              onInputChange={(_, v) => setRow(i, { internalEvent: v })}
              renderInput={(params) => <TextField {...params} label="Conversion Type" />}
            />
          </Grid>
          <Grid size={{ xs: 1 }} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">-&gt;</Typography>
          </Grid>
          <Grid size={{ xs: 5 }}>
            <Autocomplete
              size="small" freeSolo options={outboundOptions || []}
              value={row.outboundEvent}
              onInputChange={(_, v) => setRow(i, { outboundEvent: v })}
              renderInput={(params) => <TextField {...params} label="Reports As" />}
            />
          </Grid>
          <Grid size={{ xs: 1 }}>
            <IconButton size="small" color="error" onClick={() => emit(rows.filter((_, j) => j !== i))}>
              <RemoveCircleOutline sx={{ fontSize: 16 }} />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </Box>
  );
}
